import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Equal, Pencil, X } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * The building blocks of the "explain one row" right-side drawer, extracted from
 * `UsedDishesDrawer` (reports → utilization) so the leakage drawer renders the
 * identical chrome and cards instead of a look-alike copy.
 *
 * Not to be confused with `InventorySlideOver.tsx` — that one is a form shell: it
 * forces a footer with action buttons and hardcodes its hexes. These parts are for
 * read-only explanation panels and compose from the semantic tokens.
 */

interface SlideOverShellProps {
  onClose: () => void;
  children: React.ReactNode;
  /** Tailwind max-width class for the panel. Defaults to the 584px drawer width. */
  widthClass?: string;
  /** Extra content in the header bar, right of the back arrow. */
  header?: React.ReactNode;
  closeLabel?: string;
  /**
   * A modal opened from inside this drawer is stacked on top of it. Its own dim + shadow
   * are then dropped (they would double up with the one above) and Escape is handed to
   * that modal, so one keypress no longer closes both layers.
   */
  overlayOpen?: boolean;
  onboardingId?: string;
}

/**
 * Backdrop + right-hand panel + header bar + scrolling body. Render it only while the
 * drawer is open (callers return `null` when there is nothing to show) — mounting is
 * what arms the Escape handler and the background scroll lock.
 */
export const SlideOverShell: React.FC<SlideOverShellProps> = ({
  onClose,
  children,
  widthClass = 'max-w-[584px]',
  header,
  closeLabel,
  overlayOpen = false,
  onboardingId,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    // Re-applied whenever `overlayOpen` flips back: a modal that stacked on top of this
    // drawer clears the lock on its own unmount, and the drawer still needs it.
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !overlayOpen) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, overlayOpen]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {!overlayOpen && <div className="absolute inset-0 bg-black/60 dark:bg-black/60" onClick={onClose} />}

      <div
        data-onboarding={onboardingId}
        className={cn(
          'relative flex flex-col w-full h-full bg-background dark:bg-background-dark animate-in slide-in-from-right',
          !overlayOpen && 'shadow-2xl',
          widthClass
        )}
      >
        <div className="flex items-center px-4 sm:px-5 py-3 sm:py-4 border-b border-border dark:border-border-dark flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg border border-border dark:border-border-dark text-textPrimary dark:text-textPrimary-dark hover:bg-backgroundDarker dark:hover:bg-backgroundDarker-dark transition-colors"
            aria-label={closeLabel ?? t('common.close')}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          {header}
        </div>

        <div className="flex-1 overflow-y-auto hide-scrollbar px-4 sm:px-5 py-4 flex flex-col min-h-0">{children}</div>
      </div>
    </div>
  );
};

export const SectionTitle: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <h3 className={cn('text-base leading-[30px] font-semibold text-textPrimary dark:text-textPrimary-dark', className)}>{children}</h3>
);

export const OverviewCard: React.FC<{
  label: string;
  value: string;
  sub?: string;
  tone?: 'bad' | 'warn' | 'neutral';
}> = ({ label, value, sub, tone = 'neutral' }) => (
  <div className="rounded-xl border border-border dark:border-border-dark bg-background dark:bg-background-dark px-3 sm:px-5 py-3 flex flex-col min-w-0 shadow-[0px_1px_2px_0px_#1018280D] dark:shadow-[0px_1px_2px_0px_#00000040]">
    <span className="mb-2 sm:mb-3 text-[15px] sm:text-lg font-semibold text-textPrimary dark:text-textPrimary-dark">{label}</span>
    <span
      className={cn(
        'text-xl sm:text-2xl leading-7 sm:leading-[38px] font-semibold break-words',
        tone === 'bad'
          ? 'text-textRed dark:text-textRed-dark'
          : tone === 'warn'
          ? 'text-[#B54708] dark:text-[#FDB022]'
          : 'text-textPrimary dark:text-textPrimary-dark'
      )}
    >
      {value}
    </span>
    {sub && <span className="text-[11px] sm:text-xs text-muted dark:text-muted-dark break-words">{sub}</span>}
  </div>
);

export const FormulaOperator: React.FC<{ variant?: 'multiply' | 'equals' }> = ({ variant = 'multiply' }) => (
  <span className="flex items-center justify-center w-5 sm:w-7 h-5 sm:h-7 rounded-full text-[#069A6E] dark:text-[#069A6E] bg-[#E7F3F0] dark:bg-[#16A34A1A]">
    {variant === 'equals' ? (
      <Equal className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={2.5} />
    ) : (
      <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={2.5} />
    )}
  </span>
);

export const FormulaChip: React.FC<{ label: string; sub?: string; highlight?: boolean }> = ({ label, sub, highlight }) => (
  <div
    className={cn(
      'flex flex-col items-center justify-center rounded-lg px-1.5 sm:px-2.5 py-1.5 min-h-[48px] sm:min-h-[57px] min-w-0',
      highlight
        ? 'bg-[#EDFCF5] dark:bg-[#16A34A1A] flex-1 sm:w-full sm:max-w-[180px]'
        : 'bg-backgroundDarker dark:bg-backgroundDarker-dark flex-1 sm:flex-none sm:w-[110px] max-w-[110px] sm:max-w-none'
    )}
  >
    <span className="text-[13px] sm:text-base font-semibold leading-5 sm:leading-6 text-center break-words text-[#0E8965] dark:text-darkGreenColor-dark">
      {label}
    </span>
    {sub && (
      <span className="mt-0.5 text-[11px] sm:text-sm font-medium text-muted dark:text-muted-dark text-center whitespace-normal sm:whitespace-nowrap">
        {sub}
      </span>
    )}
  </div>
);

/**
 * One `per unit × units = total` formula card. Every figure arrives pre-formatted so
 * the card stays free of any unit/currency logic — the caller owns that.
 */
export const BreakdownCard: React.FC<{
  title: string;
  /** Marker rendered right of the title (e.g. the "via alternative" arrow). */
  titleBadge?: React.ReactNode;
  /** Headline value on the right, already formatted. */
  value?: string | null;
  onEdit?: () => void;
  onboardingId?: string;
  perUnit: string;
  perUnitSub: string;
  units: string;
  unitsSub: string;
  total: string;
  /** Muted explanatory lines under the title — the prep breakdown that says where a
   *  per-unit figure came from. Absent/empty renders nothing at all, so a card without
   *  them is byte-identical to one from before this slot existed. */
  notes?: string[];
}> = ({ title, titleBadge, value, onEdit, onboardingId, perUnit, perUnitSub, units, unitsSub, total, notes }) => {
  const { t } = useTranslation();

  return (
    <div className="rounded-xl bg-background dark:bg-background-dark p-3 sm:p-4 min-w-0">
      <div className="flex items-start justify-between gap-2 sm:gap-3 mb-3 sm:mb-4">
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <p className="text-base sm:text-lg font-semibold text-textPrimary dark:text-textPrimary-dark truncate">{title}</p>
            {titleBadge}
          </div>
          {notes?.map((note) => (
            // Wraps rather than truncates: a prep chain is the explanation itself, so
            // clipping it defeats the point — and it must stay readable on mobile.
            <span key={note} className="mt-1 text-xs sm:text-sm font-medium text-muted dark:text-muted-dark break-words">
              {note}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {value && <span className="text-base sm:text-lg font-bold text-textPrimary dark:text-textPrimary-dark">{value}</span>}
          {onEdit && (
            <button
              type="button"
              onClick={onEdit}
              data-onboarding={onboardingId}
              className="inline-flex items-center gap-1 h-8 px-2 rounded-lg border border-border dark:border-border-dark text-xs font-medium text-textSecondary dark:text-textSecondary-dark hover:bg-backgroundDarker dark:hover:bg-backgroundDarker-dark transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" />
              {t('common.edit')}
            </button>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 sm:gap-2 min-w-0">
        <FormulaChip label={perUnit} sub={perUnitSub} />
        <FormulaOperator variant="multiply" />
        <FormulaChip label={units} sub={unitsSub} />
        <FormulaOperator variant="equals" />
        <FormulaChip label={total} highlight />
      </div>
    </div>
  );
};
